import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { LOGIN_ERRORS, LoginResponse } from '@/auth/interfaces/login-response';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants/networking.constants';
import {
  HTTPResStatusCodeEnum
} from '@/shared/interfaces/erp/global/axios.enum';
import {
  AlertSeverityEnum,
} from '@/shared/interfaces/erp/global/errors.interface';
import { handleAxiosError } from '@/shared/utils';
import {
  useAuthStore,
} from '@/store/auth/auth.store';
import {
  useDosFactorLogin,
} from '@/store/auth/dos-factor-login.store';
import { useMfaStatusStore } from '@/store/auth/mfa-status.store';
import {
  useFreeLogin,
} from '@/store/auth/login.store';
import {
  useUserModulesStore,
} from '@/store/auth/user-modules.store';
import {
  useUserPermissionsStore,
} from '@/store/auth/user-permissions.store';
import { useUiStore } from '@/store/ui/ui.store';
import { useUiConfirmModalStore } from '@/store/ui/confirm-modal.store';
import { toast } from 'react-toastify';

const { post, get } = erpApi();

export type ChangePasswordData = {
  old_password: string;
  password: string;
};

export type LoginData = {
  username: string;
  password: string;
  forzar?: boolean;
  empresa?: string; // empresa
  nombre_comercial?: string; // form
};

export const useLogin = () => {

  ///* global state
  const setAuth = useAuthStore(s => s.setAuth);
  const logOutWithoutToken = useAuthStore(s => s.logOutWithoutToken);
  const setPermissions = useUserPermissionsStore(s => s.setPermissions);
  const setModulos = useUserModulesStore(s => s.setModules);

  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  // const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationKey: ['login'],

    mutationFn: (data: LoginData) => {
      return login(data);
    },

    onSuccess: async (res, variables) => {

      const loginResponse: any = res.loginResponse.data

      // 2FA: si el backend devuelve challenge, abrir el diálogo OTP en vez de autenticar
      if (loginResponse && loginResponse.dos_factor_requerido) {
        useDosFactorLogin.getState().open({
          challengeToken: loginResponse.challenge_token,
          metodo: loginResponse.metodo,
          emailMascarado: loginResponse.email_mascarado,
          emailEnvioOk: loginResponse.email_envio_ok,
          emailEnvioError: loginResponse.email_envio_error,
          loginData: variables,
        });
        return;
      }

      setAuth(
        loginResponse.token,
        {
          ...loginResponse.profile,
          email: loginResponse?.email,
          username: loginResponse?.username,
          user_id: loginResponse?.id,
        }
      );
      setPermissions(loginResponse.permisos);
      setModulos(loginResponse.modulos);

      useMfaStatusStore.getState().setStatus(
        Boolean(loginResponse?.mfa_setup_pendiente),
        loginResponse?.mfa_grace_until ?? null,
      );

      toast('Inicio de sesión exitoso', {
        type: 'success',
      });





    },

    onError: (err, variables)=> {
      if (isAxiosError(err)) {
        // // // Wrong password --------------
        const attempt = err.response?.data?.data?.intentos_fallidos;
        const isWrongPasswordResponse = isFinite(attempt);
        if (isWrongPasswordResponse) {
          if (attempt === LOGIN_ERRORS.wrongPassword.allowedAttempts) {
            useFreeLogin.getState().setBlocked(true);
            toast('Has excedido el número de intentos fallidos permitidos, tu usuario ha sido bloqueado.', {
              type: 'error',
              autoClose: 5000,
            });
            return;
          }

          if (attempt === LOGIN_ERRORS.wrongPassword.showMsgAttempt) {
            setConfirmDialog({
              isOpen: true,
              title: 'Te queda un intento',
              subtitle:
                'Si fallas en el siguiente intento, tu usuario será bloqueado y deberás contactar a soporte técnico para desbloquearlo',
              cancelTextBtn: 'Cerrar',
              confirmTextBtn: 'Entendido',
              onConfirm: () => {
                setConfirmDialogIsOpen(false);
              },
            });
            return;
          }
        }

        // // // Session is active --------------
        if (err.response?.status === HTTPResStatusCodeEnum.ACTIVE_SESSION) {
          setConfirmDialog({
            isOpen: true,
            title: `Este usuario ya tiene una sesión activa
            en la dirección IP: ${err.response?.data?.data?.ip}`,
            subtitle:
              '¿Desea cerrar la sesión activa y continuar con la nueva sesión?',
            onConfirm: async () => {
              try {


                const res = await login({
                  ...variables, forzar: true,
                });

                const loginResponse: any = res.loginResponse.data

                // 2FA: si tras forzar el reemplazo de sesión el backend
                // devuelve challenge (HTTP 206), abrir el diálogo OTP en
                // vez de autenticar — sin este check el modal MFA nunca
                // se mostraba y el usuario quedaba atascado.
                if (loginResponse && loginResponse.dos_factor_requerido) {
                  useDosFactorLogin.getState().open({
                    challengeToken: loginResponse.challenge_token,
                    metodo: loginResponse.metodo,
                    emailMascarado: loginResponse.email_mascarado,
                    emailEnvioOk: loginResponse.email_envio_ok,
                    emailEnvioError: loginResponse.email_envio_error,
                    loginData: { ...variables, forzar: true },
                  });
                  return;
                }

                setAuth(
                  loginResponse.token,
                  {
                    ...loginResponse.profile,
                    email: loginResponse?.email,
                    username: loginResponse?.username,
                    user_id: loginResponse?.id,
                  }
                );
                setPermissions(loginResponse.permisos);
                setModulos(loginResponse.modulos);

                useMfaStatusStore.getState().setStatus(
                  Boolean(loginResponse?.mfa_setup_pendiente),
                  loginResponse?.mfa_grace_until ?? null,
                );

                toast('Inicio de sesión exitoso', {
                  type: 'success',
                });



              } catch (error) {
                handleAxiosError(error);
              } finally {
                setConfirmDialogIsOpen(false);
                setIsGlobalLoading(false);
              }
            },
          });

          return;
        }

        toast(err.response?.data?.message || 'Error al iniciar sesión', {
          type: 'error',
        });
        logOutWithoutToken();
      }
      logOutWithoutToken();
    },

    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};



export const useChangePassword = () => {
  ///* global state
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationKey: ['change-password'],

    mutationFn: (data: ChangePasswordData) => {
      return changePassword(data);
    },

    onSuccess: async res => {
      const changePasswordResponse = res;
      if (changePasswordResponse)
        setAlert(true, 'Contraseña actualizada', AlertSeverityEnum.success);
    },

    onError: err => {
      if (isAxiosError(err)) {

        return err && setAlert(
          true,
          'Ocurrió un problema al intentar cambiar la contraseña',
          AlertSeverityEnum.error
        );
      }
    },

    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* Axios ===================================

export const login = async (data: LoginData) => {
  const setAuthToken = useAuthStore.getState().setToken;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;


  const newData = { ...data, empresa: data?.nombre_comercial }
  delete newData?.nombre_comercial;

  setIsGlobalLoading(true);
  const loginRes = await post<LoginResponse>(
    `${urlBackend}api/v1/login/`,
    newData,
    true
  );

  const { token } = (loginRes?.data as any) ?? {};
  if (token) setAuthToken(token); // no setear si es respuesta 2FA (sin token aún)

  setIsGlobalLoading(false);

  return {
    loginResponse: loginRes,
  };
};

export const logOutAxios = async () => {
  return get(`${urlBackend}api/v1/logout/`, true);
};

export const changePassword = async (data: ChangePasswordData) => {
  return post(`${urlBackend}api/v1/cambiar-password/`, data, true);
};

export const unBlockUserInvalidPassword = async (id: string) => {
  try {
    return await get(`/desbloquear-usuario/${id}/`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};
